import React, { useState, useEffect, useContext } from 'react';
import DatahubAPI from '../../api/DatahubAPI';
import CircularProgress from '@mui/material/CircularProgress';
import CyberHitBreakdown from '../../components/CyberProtection/CyberHitBreakdown';
import { exportToCSV } from 'components/Common/CSVManage';
import { useSnackbar } from 'notistack';
import Modal from 'react-modal';
import { Close } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import {
  StyledInput,
  ModuleContentTooltip,
  NextButton,
} from '../../components/Common/MUIStyledComponents';
import EntityResolution from 'components/Common/EntityResolution';
import { GlobalContext } from 'providers/GlobalProvider';
import SubscribeModal from 'components/SubscribeModal';
import { Player } from '@lottiefiles/react-lottie-player';
import CustomAutocomplete from 'components/Common/MUICustomAutocomplete';
import ModuleHeaderLayout from 'components/Common/Layouts/ModuleHeaderLayout';

const CyberProtectionReact: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const [ tableData, setTableData ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ pageNum, setPageNum ] = useState<number>(0);
  const [ selectedIndex, setSelectedIndex ] = useState<string>('Known');
  const [ selectedCountry, setSelectedCountry ] = useState<string>('All');
  const [ fileName, setFileName ] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [ fileNameModalOpen, setFileNameModalOpen ] = useState(false);
  const [ entityToggle, setEntityToggle ] = useState(false);
  const [ securitySelected, setSecuritySelected ] = useState(false);
  const [ userHasCyber, setUserHasCyber ] = useState(false);
  const [ matchedTeamWithStripe, setMatchedTeamWithStripe ] =
    useState<string>('');
  const [ activeSub, setActiveSub ] = useState(false);
  const [ toClear, setToClear ] = useState(false);

  const checkForCyber = () => {
    if (
      cur_user.selected_team?.attributes?.tier?.includes('enterprise') ||
      cur_user.selected_team?.attributes?.tier?.includes('standard') ||
      cur_user.selected_team?.attributes?.tier?.includes('pro')
    ) {
      setUserHasCyber(true);
    }
    if (cur_user.stripe_cus_info) {
      cur_user.stripe_cus_info.map((cus: any) => {
        if (cus.name === cur_user.selected_team.name) {
          setMatchedTeamWithStripe(cus.id);
          cus.subscriptions.data.map((sub: any) => {
            if (
              sub.metadata.moduleName === 'BITE Standard' ||
              sub.metadata.moduleName === 'BITE Pro' ||
              sub.metadata.moduleName === 'BITE Enterprise'
            ) {
              setActiveSub(true);
            }
          });
        }
      });
    }
  };

  useEffect(() => {
    checkForCyber();
  }, [ activeSub ]);

  // FORM VALUES
  const [ formValues, setFormValues ] = useState({
    searchValue: '',
    country: '',
    fuzziness: 0,
    baseline: '',
  });

  // file modal
  const handleFileNameChange = (e: any) => {
    setFileName(e.target.value);
  };

  const handleFileNameClose = () => {
    setFileNameModalOpen(false);
    setFileName('');
  };

  const toggleModal = () => {
    setFileNameModalOpen(true);
  };

  const knownEVHeaders = [
    'CVE ID',
    'Vendor Project',
    'Product',
    'Vulnerability Name',
    'Required Action',
  ];
  const embargoedHeaders = [
    'IP Address',
    'Country',
    'Country Code',
    'Embargo Status',
  ];
  const marketsHeaders = [
    'Market Name',
    'Description',
    'Website',
    'Market Location',
  ];
  const cisHeaders = [
    'CIS Control',
    'CIS Subcontrol',
    'Asset Type',
    'Security Function',
    'Title',
    'Description',
  ];
  const cmccHeaders = [ 'Level', 'Identifier', 'Domain', 'Description' ];
  const isoHeaders = [
    'ISO Control Family',
    'ID',
    'Control Name',
    'Description',
  ];
  const nistHeaders = [
    'Control Identifier',
    'Control (or Control Enhancement) Name',
    'Control Text',
    'Discussion',
    'Related Controls',
  ];

  const handleCSV = () => {
    if (selectedIndex === 'CIS') {
      if (tableData.data.hits.hits.length >= 1) {
        exportToCSV(fileName, [ ...tableData.data.hits.hits ], cisHeaders, 'CIS');
      } else {
        enqueueSnackbar('No Result Data to Export', { variant: 'error' });
      }
    }
    if (selectedIndex === 'CMCC') {
      if (tableData.data.hits.hits.length >= 1) {
        exportToCSV(
          fileName,
          [ ...tableData.data.hits.hits ],
          cmccHeaders,
          'CMCC',
        );
      } else {
        enqueueSnackbar('No Result Data to Export', { variant: 'error' });
      }
    }
    if (selectedIndex === 'ISO') {
      if (tableData.data.hits.hits.length >= 1) {
        exportToCSV(fileName, [ ...tableData.data.hits.hits ], isoHeaders, 'ISO');
      } else {
        enqueueSnackbar('No Result Data to Export', { variant: 'error' });
      }
    }
    if (selectedIndex === 'NIST') {
      if (tableData.data.hits.hits.length >= 1) {
        exportToCSV(
          fileName,
          [ ...tableData.data.hits.hits ],
          nistHeaders,
          'NIST',
        );
      } else {
        enqueueSnackbar('No Result Data to Export', { variant: 'error' });
      }
    }
    if (selectedIndex === 'Known') {
      if (tableData.data.hits.hits.length >= 1) {
        exportToCSV(
          fileName,
          [ ...tableData.data.hits.hits ],
          knownEVHeaders,
          'knownEv',
        );
      } else {
        enqueueSnackbar('No Result Data to Export', { variant: 'error' });
      }
    }
    if (selectedIndex === 'Embargoed') {
      if (tableData.data.hits.hits.length >= 1) {
        exportToCSV(
          fileName,
          [ ...tableData.data.hits.hits ],
          embargoedHeaders,
          'embargoed',
        );
      } else {
        enqueueSnackbar('No Result Data to Export', { variant: 'error' });
      }
    }
    if (selectedIndex === 'Markets') {
      if (tableData.data.hits.hits.length >= 1) {
        exportToCSV(
          fileName,
          [ ...tableData.data.hits.hits ],
          marketsHeaders,
          'markets',
        );
      } else {
        enqueueSnackbar('No Result Data to Export', { variant: 'error' });
      }
    }
  };

  const getInitialResults = async() => {
    const res = await DatahubAPI.cyber({
      indexName: selectedIndex,
      agg: true,
      pageNum: 0,
    });
    if (res?.success) {
      setTableData({ isLoading: false, data: res?.result?.result?.body });
    }
  };

  const queryResults = async(values: any, page: number) => {
    let fuzzyValue = '';
    if (entityToggle) {
      fuzzyValue = 'AUTO';
    } else {
      fuzzyValue = '0';
    }
    if (selectedIndex === 'Known') {
      const res = await DatahubAPI.cyber({
        indexName: selectedIndex,
        agg: true,
        primary: values.searchValue,
        pageNum: page,
        fuzziness: fuzzyValue,
      });
      if (res?.success) {
        setTableData({ isLoading: false, data: res?.result?.result?.body });
      } else {
        enqueueSnackbar(
          'Error retrieving data. Cyber Protection may be being updated',
          { variant: 'error', autoHideDuration: 5000 },
        );
      }
    }
    if (selectedIndex === 'Embargoed') {
      const res = await DatahubAPI.cyber({
        indexName: selectedIndex,
        agg: true,
        primary: values.searchValue,
        cyberCountry: [ values.country ],
        pageNum: page,
        fuzziness: fuzzyValue,
      });
      if (res?.success) {
        setTableData({ isLoading: false, data: res?.result?.result?.body });
      } else {
        enqueueSnackbar(
          'Error retrieving data. Cyber Protection may be being updated',
          { variant: 'error', autoHideDuration: 5000 },
        );
      }
    }
    if (selectedIndex === 'Markets') {
      const res = await DatahubAPI.cyber({
        indexName: selectedIndex,
        agg: true,
        primary: values.searchValue,
        pageNum: page,
        fuzziness: fuzzyValue,
      });
      if (res?.success) {
        setTableData({ isLoading: false, data: res?.result?.result?.body });
      } else {
        enqueueSnackbar(
          'Error retrieving data. Cyber Protection may be being updated',
          { variant: 'error', autoHideDuration: 5000 },
        );
      }
    }
    if (selectedIndex === 'CIS') {
      const res = await DatahubAPI.cyber({
        indexName: selectedIndex,
        agg: true,
        primary: values.searchValue,
        pageNum: page,
        fuzziness: fuzzyValue,
      });
      if (res?.success) {
        setTableData({ isLoading: false, data: res?.result?.result?.body });
      } else {
        enqueueSnackbar(
          'Error retrieving data. Cyber Protection may be being updated',
          { variant: 'error', autoHideDuration: 5000 },
        );
      }
    }
    if (selectedIndex === 'CMCC') {
      const res = await DatahubAPI.cyber({
        indexName: selectedIndex,
        agg: true,
        primary: values.searchValue,
        pageNum: page,
        fuzziness: fuzzyValue,
      });
      if (res?.success) {
        setTableData({ isLoading: false, data: res?.result?.result?.body });
      } else {
        enqueueSnackbar(
          'Error retrieving data. Cyber Protection may be being updated',
          { variant: 'error', autoHideDuration: 5000 },
        );
      }
    }
    if (selectedIndex === 'ISO') {
      const res = await DatahubAPI.cyber({
        indexName: selectedIndex,
        agg: true,
        primary: values.searchValue,
        pageNum: page,
        fuzziness: fuzzyValue,
      });
      if (res?.success) {
        setTableData({ isLoading: false, data: res?.result?.result?.body });
      } else {
        enqueueSnackbar(
          'Error retrieving data. Cyber Protection may be being updated',
          { variant: 'error', autoHideDuration: 5000 },
        );
      }
    }
    if (selectedIndex === 'NIST') {
      const res = await DatahubAPI.cyber({
        indexName: selectedIndex,
        agg: true,
        primary: values.searchValue,
        pageNum: page,
        fuzziness: fuzzyValue,
      });
      if (res?.success) {
        setTableData({ isLoading: false, data: res?.result?.result?.body });
      } else {
        enqueueSnackbar(
          'Error retrieving data. Cyber Protection may be being updated',
          { variant: 'error', autoHideDuration: 5000 },
        );
      }
    }
    if (selectedIndex === 'FED') {
      const res = await DatahubAPI.cyber({
        indexName: selectedIndex,
        agg: true,
        primary: values.searchValue,
        pageNum: page,
        fuzziness: fuzzyValue,
        baseline: values.baseline,
      });
      if (res?.success) {
        setTableData({ isLoading: false, data: res?.result?.result?.body });
      } else {
        enqueueSnackbar(
          'Error retrieving data. Cyber Protection may be being updated',
          { variant: 'error', autoHideDuration: 5000 },
        );
      }
    }
  };

  const handleIndexChange = (index: string) => {
    switch (index) {
    case 'Known':
      setSelectedIndex('Known');
      setSecuritySelected(false);
      break;
    case 'Embargoed':
      setSelectedIndex('Embargoed');
      setSecuritySelected(false);
      break;
    case 'Markets':
      setSelectedIndex('Markets');
      setSecuritySelected(false);
      break;
    case 'CIS':
      setSelectedIndex('CIS');
      setSecuritySelected(false);
      break;
    case 'CMCC':
      setSelectedIndex('CMCC');
      setSecuritySelected(false);
      break;
    case 'ISO':
      setSelectedIndex('ISO');
      setSecuritySelected(false);
      break;
    case 'NIST':
      setSelectedIndex('NIST');
      setSecuritySelected(false);
      break;
    case 'FED':
      setSelectedIndex('FED');
      setSecuritySelected(false);
      break;
    default:
      setSelectedIndex('Known');
      setSecuritySelected(false);
    }
  };

  const handleCountryChange = (country: string) => {
    switch (country) {
    case 'All':
      setSelectedCountry('All');
      setFormValues({ ...formValues, country: '' });
      break;
    case 'Cuba':
      setSelectedCountry('Cuba');
      setFormValues({ ...formValues, country: 'Cuba' });
      break;
    case 'Islamic Republic of Iran':
      setSelectedCountry('Islamic Republic of Iran');
      setFormValues({ ...formValues, country: 'Iran (Islamic Republic of)' });
      break;
    case 'North Korea':
      setSelectedCountry('North Korea');
      setFormValues({
        ...formValues,
        country: 'Korea (Democratic People\'s Republic of)',
      });
      break;
    case 'Syrian Arab Republic':
      setSelectedCountry('Syrian Arab Republic');
      setFormValues({ ...formValues, country: 'Syrian Arab Republic' });
      break;
    default:
      setSelectedCountry('All');
      setFormValues({ ...formValues, country: '' });
    }
  };

  const clearFormAndResults = async() => {
    setFormValues({
      searchValue: '',
      country: '',
      fuzziness: 0,
      baseline: '',
    });
    setToClear(true);
    await getInitialResults();
    setToClear(false);
  };

  useEffect(() => {
    setFormValues({
      searchValue: '',
      country: '',
      fuzziness: 0,
      baseline: '',
    });
    getInitialResults();
  }, [ selectedIndex ]);

  useEffect(() => {
    if (selectedCountry === 'All') {
      getInitialResults();
    } else {
      queryResults(formValues, pageNum);
    }
  }, [ selectedCountry ]);

  const handleEnterPress = (e: any) => {
    if (e.key === 'Enter' || e.which === 13) {
      queryResults(formValues, pageNum);
    }
  };

  useEffect(() => {
    if (securitySelected) {
      setSelectedIndex('CIS');
    }
  }, [ securitySelected ]);

  const handleBaselineSelect = async(e: any) => {
    setFormValues({ ...formValues, baseline: e.target.value });
  };

  useEffect(() => {
    queryResults(formValues, pageNum);
  }, [ formValues.baseline ]);

  const helpContent = (
    <p className='module-header-tooltip-text'>
      The Cyber Protection module serves as a database where you can search
      lists related to cyber controls, IP addresses, and counterfeit markets.
      Use the search bar to look up specific queries.
    </p>
  );

  return (
    <ModuleHeaderLayout title='Cyber Protection' helpContent={helpContent}>
      {activeSub && !userHasCyber ? (
        <div className='no-license-modal'>
          <Player
            autoplay
            loop
            src='https://lottie.host/3c23c1ad-7f71-4e3d-8f19-69154715dbdc/SdWWjCmU1J.json'
            style={{ width: '20%' }}
          ></Player>
          <Typography
            variant='h6'
            className='text-white'
            style={{
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            }}
          >
            You need an assigned license to access this module
          </Typography>
          <Typography
            variant='h6'
            style={{
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              marginTop: '20px',
              color: '#BDBEC1',
            }}
          >
            Visit Account &gt; Users &gt; to assign an available license to your
            user or contact your team admin
          </Typography>
        </div>
      ) : !userHasCyber ? (
        <SubscribeModal
          name='Cyber Protection'
          description='Search a list of tracked cyber risks'
          price={149}
          curUser={cur_user}
          stripeTeam={matchedTeamWithStripe}
          activeSub={activeSub}
        />
      ) : (
        <></>
      )}
      <div className={`os-iframe${!userHasCyber ? '-blur' : ''}`}>
        <div className='bl-index-select'>
          <div
            className='bl-index-item'
            onClick={() => handleIndexChange('Known')}
          >
            <ModuleContentTooltip
              placement='top'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    'display': 'flex',
                    'padding': 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    'flexDirection': 'column',
                    'alignItems': 'flex-start',
                    'alignSelf': 'stretch',
                    'borderRadius': 'var(--radius-md, 8px)',
                    'background': 'var(--Background-200, #202227)',
                    'maxWidth': '400px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, #202227)',
                    },
                  },
                },
              }}
              title={
                <p className='module-header-tooltip-text'>
                  Use the search bar to look up specific queries.
                </p>
              }
            >
              <div className='bl-item-title'>
                Common Vulnerabilities and Exposures
              </div>
            </ModuleContentTooltip>
            <div className='bl-item-svg'>
              {selectedIndex === 'Known' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='206'
                  height='4'
                  viewBox='0 0 206 4'
                  fill='none'
                >
                  <path
                    d='M2 2L204.333 1.99998'
                    stroke='url(#paint0_linear_2173_3230)'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_2173_3230'
                      x1='2'
                      y1='2'
                      x2='204.333'
                      y2='1.99977'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#4CC9F0' />
                      <stop offset='0.453125' stopColor='#4895EF' />
                      <stop offset='1' stopColor='#4361EE' />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='207'
                  height='4'
                  viewBox='0 0 207 4'
                  fill='none'
                >
                  <path
                    d='M2.33337 2L204.667 2.00002'
                    stroke='#4A4C51'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </div>
          </div>
          <div
            className='bl-index-item'
            onClick={() => handleIndexChange('Embargoed')}
          >
            <ModuleContentTooltip
              placement='top'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    'display': 'flex',
                    'padding': 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    'flexDirection': 'column',
                    'alignItems': 'flex-start',
                    'alignSelf': 'stretch',
                    'borderRadius': 'var(--radius-md, 8px)',
                    'background': 'var(--Background-200, #202227)',
                    'maxWidth': '400px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, #202227)',
                    },
                  },
                },
              }}
              title={
                <p className='module-header-tooltip-text'>
                  Search on all embargoed IPs or click through countries to
                  search specific countries&apos; IP addresses. Use the search
                  bar to look up specific queries.
                </p>
              }
            >
              <div className='bl-item-title'>Embargoed IP Addresses</div>
            </ModuleContentTooltip>
            <div className='bl-item-svg'>
              {selectedIndex === 'Embargoed' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='206'
                  height='4'
                  viewBox='0 0 206 4'
                  fill='none'
                >
                  <path
                    d='M2 2L204.333 1.99998'
                    stroke='url(#paint0_linear_2173_3230)'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_2173_3230'
                      x1='2'
                      y1='2'
                      x2='204.333'
                      y2='1.99977'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#4CC9F0' />
                      <stop offset='0.453125' stopColor='#4895EF' />
                      <stop offset='1' stopColor='#4361EE' />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='207'
                  height='4'
                  viewBox='0 0 207 4'
                  fill='none'
                >
                  <path
                    d='M2.33337 2L204.667 2.00002'
                    stroke='#4A4C51'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </div>
          </div>
          <div
            className='bl-index-item'
            onClick={() => handleIndexChange('Markets')}
          >
            <ModuleContentTooltip
              placement='top'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    'display': 'flex',
                    'padding': 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    'flexDirection': 'column',
                    'alignItems': 'flex-start',
                    'alignSelf': 'stretch',
                    'borderRadius': 'var(--radius-md, 8px)',
                    'background': 'var(--Background-200, #202227)',
                    'maxWidth': '400px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, #202227)',
                    },
                  },
                },
              }}
              title={
                <p className='module-header-tooltip-text'>
                  Use the search bar to look up specific queries.
                </p>
              }
            >
              <div className='bl-item-title'>Notorious Markets</div>
            </ModuleContentTooltip>
            <div className='bl-item-svg'>
              {selectedIndex === 'Markets' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='206'
                  height='4'
                  viewBox='0 0 206 4'
                  fill='none'
                >
                  <path
                    d='M2 2L204.333 1.99998'
                    stroke='url(#paint0_linear_2173_3230)'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_2173_3230'
                      x1='2'
                      y1='2'
                      x2='204.333'
                      y2='1.99977'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#4CC9F0' />
                      <stop offset='0.453125' stopColor='#4895EF' />
                      <stop offset='1' stopColor='#4361EE' />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='207'
                  height='4'
                  viewBox='0 0 207 4'
                  fill='none'
                >
                  <path
                    d='M2.33337 2L204.667 2.00002'
                    stroke='#4A4C51'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </div>
          </div>
          <div
            className='bl-index-item'
            onClick={() => setSecuritySelected(!securitySelected)}
          >
            <ModuleContentTooltip
              placement='top'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    'display': 'flex',
                    'padding': 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    'flexDirection': 'column',
                    'alignItems': 'flex-start',
                    'alignSelf': 'stretch',
                    'borderRadius': 'var(--radius-md, 8px)',
                    'background': 'var(--Background-200, #202227)',
                    'maxWidth': '400px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, #202227)',
                    },
                  },
                },
              }}
              title={
                <p className='module-header-tooltip-text'>
                  Use the search bar to look up specific queries. Select which
                  list you want to search under the search bar.
                </p>
              }
            >
              <div className='bl-item-title'>Security Controls</div>
            </ModuleContentTooltip>
            <div className='bl-item-svg'>
              {securitySelected ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='206'
                  height='4'
                  viewBox='0 0 206 4'
                  fill='none'
                >
                  <path
                    d='M2 2L204.333 1.99998'
                    stroke='url(#paint0_linear_2173_3230)'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_2173_3230'
                      x1='2'
                      y1='2'
                      x2='204.333'
                      y2='1.99977'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#4CC9F0' />
                      <stop offset='0.453125' stopColor='#4895EF' />
                      <stop offset='1' stopColor='#4361EE' />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='207'
                  height='4'
                  viewBox='0 0 207 4'
                  fill='none'
                >
                  <path
                    d='M2.33337 2L204.667 2.00002'
                    stroke='#4A4C51'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </div>
          </div>
        </div>
        <div className='watchlist-search'>
          <input
            id='searchVal'
            placeholder='Search here...'
            type='text'
            className='wl-search-input'
            name='searchValue'
            value={formValues.searchValue}
            onChange={(e) =>
              setFormValues({
                ...formValues,
                searchValue: e.target.value,
              })
            }
            onKeyUp={handleEnterPress}
          />
          <Button
            className='wl-search-btn'
            onClick={() => queryResults(formValues, pageNum)}
          >
            Search
          </Button>
        </div>
        <div className='filters-header'>
          <div className='filters-text'>
            <EntityResolution
              entityToggle={entityToggle}
              setEntityToggle={setEntityToggle}
            />
          </div>
          <div className='clear-module-form' onClick={clearFormAndResults}>
            <div className='clear-module-text'>Clear All</div>
            <div className='clear-form-icon'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 18 18'
                fill='none'
              >
                <path
                  d='M11.25 5.13281C11.25 5.13281 12.1064 4.71094 9 4.71094C7.88748 4.71094 6.79995 5.04084 5.87492 5.65892C4.94989 6.277 4.22892 7.15551 3.80318 8.18334C3.37744 9.21118 3.26604 10.3422 3.48309 11.4333C3.70013 12.5245 4.23586 13.5267 5.02253 14.3134C5.8092 15.1001 6.81148 15.6358 7.90262 15.8529C8.99376 16.0699 10.1248 15.9585 11.1526 15.5328C12.1804 15.107 13.0589 14.386 13.677 13.461C14.2951 12.536 14.625 11.4485 14.625 10.3359'
                  stroke='#F5FAFF'
                  strokeWidth='1.5'
                  strokeMiterlimit='10'
                  strokeLinecap='round'
                />
                <path
                  d='M9 2.03906L11.8125 4.85156L9 7.66406'
                  stroke='#F5FAFF'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
          </div>
        </div>
        {selectedIndex === 'Embargoed' && (
          <div className='embargoed-options-container'>
            <div className='select-one-text'>Select One</div>
            <div className='embargoed-country-select-container'>
              <div
                className={`country-button-option${selectedCountry === 'All' ? '-selected' : ''
                }`}
                onClick={() => handleCountryChange('All')}
              >
                <div className='country-btn-text'>All Embargoed Countries</div>
              </div>
              <div
                className={`country-button-option${selectedCountry === 'Cuba' ? '-selected' : ''
                }`}
                onClick={() => handleCountryChange('Cuba')}
              >
                <div className='country-btn-text'>Cuba</div>
              </div>
              <div
                className={`country-button-option${selectedCountry === 'Islamic Republic of Iran' ?
                  '-selected' :
                  ''
                }`}
                onClick={() => handleCountryChange('Islamic Republic of Iran')}
              >
                <div className='country-btn-text'>Islamic Republic of Iran</div>
              </div>
              <div
                className={`country-button-option${selectedCountry === 'North Korea' ? '-selected' : ''
                }`}
                onClick={() => handleCountryChange('North Korea')}
              >
                <div className='country-btn-text'>North Korea</div>
              </div>
              <div
                className={`country-button-option${selectedCountry === 'Syrian Arab Republic' ? '-selected' : ''
                }`}
                onClick={() => handleCountryChange('Syrian Arab Republic')}
              >
                <div className='country-btn-text'>Syrian Arab Republic</div>
              </div>
            </div>
          </div>
        )}
        {securitySelected && (
          <div className='embargoed-options-container'>
            <div className='select-one-text'>Select One</div>
            <div className='embargoed-country-select-container'>
              <div
                className={`country-button-option${selectedIndex === 'CIS' ? '-selected' : ''
                }`}
                onClick={() => setSelectedIndex('CIS')}
              >
                <div className='country-btn-text'>CIS</div>
              </div>
              <div
                className={`country-button-option${selectedIndex === 'CMCC' ? '-selected' : ''
                }`}
                onClick={() => setSelectedIndex('CMCC')}
              >
                <div className='country-btn-text'>CMMC</div>
              </div>
              <div
                className={`country-button-option${selectedIndex === 'ISO' ? '-selected' : ''
                }`}
                onClick={() => setSelectedIndex('ISO')}
              >
                <div className='country-btn-text'>ISO 27001</div>
              </div>
              <div
                className={`country-button-option${selectedIndex === 'NIST' ? '-selected' : ''
                }`}
                onClick={() => setSelectedIndex('NIST')}
              >
                <div className='country-btn-text'>NIST 800-53</div>
              </div>
              <div
                className={`country-button-option${selectedIndex === 'FED' ? '-selected' : ''
                }`}
                onClick={() => setSelectedIndex('FED')}
              >
                <div className='country-btn-text'>FedRAMP</div>
              </div>
            </div>
          </div>
        )}
        {selectedIndex === 'FED' && (
          <CustomAutocomplete
            options={[ { key: 'High' }, { key: 'Moderate' }, { key: 'Low' } ]}
            placeholder='Select a Baseline Filter...'
            onChange={handleBaselineSelect}
            toClear={toClear}
          />
        )}
        {tableData.isLoading ? (
          <div>
            <CircularProgress style={{ color: '#0E8BFF' }} />
          </div>
        ) : (
          <CyberHitBreakdown
            toggleModal={toggleModal}
            selectedIndex={selectedIndex}
            queryResults={queryResults}
            formValues={formValues}
            pageNum={pageNum}
            setPageNum={setPageNum}
            tableData={tableData}
          />
        )}
        <Modal
          isOpen={fileNameModalOpen}
          onRequestClose={handleFileNameClose}
          ariaHideApp={false}
        >
          <div className='bite-score-file-modal'>
            <div className='display-flex justify-between width-100'>
              <Typography variant='h5'>File Name:</Typography>
              <Close onClick={handleFileNameClose} className='cursor-pointer' />
            </div>
            <div className='display-flex justify-end width-100'>
              <StyledInput
                value={fileName}
                onChange={handleFileNameChange}
                variant='outlined'
                className='margin-right-20px'
              />
              <NextButton
                variant='contained'
                color='primary'
                onClick={() => {
                  handleCSV();
                  handleFileNameClose();
                }}
              >
                Download
              </NextButton>
            </div>
          </div>
        </Modal>
      </div>
    </ModuleHeaderLayout>
  );
};

export default CyberProtectionReact;
